import { useCallback, useEffect, useMemo } from 'react';

import {
  IHostProps,
  IWixStatic,
  useEnvironment,
  useExperiments,
  useWixSdk,
} from '@wix/yoshi-flow-editor';
import { useStyles } from '@wix/tpa-settings/react';

import { EXPERIMENTS } from '@/constants';

import stylesParams from 'AiAssistantWidget/stylesParams';
import settingsParams from 'AiAssistantWidget/settingsParams';
import { mobileIconsDict } from 'AiAssistantWidget/Settings/components';
import { useWidgetSettings } from 'AiAssistantWidget/Widget/contexts';

import {
  selectLayout,
  selectMessages,
  selectComposed,
  selectOneChatComposed,
} from '../features';
import { useAppDispatch, useAppSelector } from '../store';
import { expandWidget } from '../features/layout/layoutThunks';

type MobileActionBarButtonConfig = {
  visible?: boolean;
  notifications?: boolean;
  color?: string;
  iconSvgContent?: string;
};

type HostSDK_SetMobileActionBarButton = {
  mobile: {
    setMobileActionBarButton: (
      config: MobileActionBarButtonConfig,
      cb?: () => void,
    ) => void;
    setMobileFullScreenMode: (enabled: boolean) => void;
    registerToMobileActionTriggered: (cb: () => void) => void;
  };
};

type WixSDKSetMobileActionBarButton = {
  setMobileActionBarButton: (config: MobileActionBarButtonConfig) => void;
};

type WixSDK =
  | (IWixStatic & {
      Mobile: WixSDKSetMobileActionBarButton;
      removeEventListener: (event: string, handler: () => void) => void;
    })
  | null;

export const useMobileActionBarButton = (_host: IHostProps) => {
  const host = _host as IHostProps & HostSDK_SetMobileActionBarButton;
  const { Wix } = useWixSdk() as { Wix: WixSDK };
  const { experiments } = useExperiments();

  const dispatch = useAppDispatch();
  const { isMobile, isEditor, isPreview } = useEnvironment();
  const hasUnread = useAppSelector(selectMessages.hasUnread);
  const isOpened = useAppSelector(selectLayout.isVisible);
  const shouldShowLegacyWidget = useAppSelector(
    selectComposed.shouldShowWidget,
  );
  const shouldShowOneChatWidget = useAppSelector(
    selectOneChatComposed.shouldShowWidget,
  );
  const shouldShowWidget = experiments.enabled(EXPERIMENTS.ONE_CHAT)
    ? shouldShowOneChatWidget
    : shouldShowLegacyWidget;
  const styles = useStyles();
  const settings = useWidgetSettings();

  const color = useMemo(
    () => styles.get(stylesParams.minimizedButtonBackground).value,
    [styles],
  );

  const iconSvgContent = useMemo(
    () =>
      mobileIconsDict[
        settings.get(settingsParams.minimizedButtonIcon, 'primary')
      ],
    [settings],
  );

  const setOpened = useCallback(() => {
    if (isPreview && isMobile) {
      return;
    }
    dispatch(expandWidget());
  }, [dispatch, isMobile, isPreview]);

  useEffect(() => {
    if (isEditor || !isMobile || !host?.mobile?.setMobileFullScreenMode) {
      return;
    }

    host.mobile.setMobileFullScreenMode(isOpened);
  }, [isOpened, host, isMobile, isEditor]);

  useEffect(() => {
    if (!isMobile || !host?.mobile?.registerToMobileActionTriggered) {
      return;
    }

    host.mobile.registerToMobileActionTriggered(setOpened);
  }, [host, isMobile, setOpened]);

  /**
   * this is only for Editor's mobile version as we have there old WixSDK
   */
  useEffect(() => {
    if (!isEditor || !isMobile || !Wix?.Mobile) {
      return;
    }

    Wix.Mobile.setMobileActionBarButton({
      color,
      iconSvgContent,
      visible: true, // always visible in editor
      notifications: false,
    });
  }, [isMobile, Wix, color, iconSvgContent, isEditor]);

  /**
   * this is only for live site as we have there hostSDK and don't have old WixSDK
   */
  useEffect(() => {
    if (isEditor || !isMobile || !host?.mobile?.setMobileActionBarButton) {
      return;
    }

    host.mobile.setMobileActionBarButton({
      color,
      iconSvgContent,
      visible: !isOpened && shouldShowWidget,
      notifications: hasUnread,
    });
  }, [
    host,
    isMobile,
    hasUnread,
    isOpened,
    color,
    shouldShowWidget,
    iconSvgContent,
    dispatch,
    isEditor,
  ]);
};
